import React, { useState, useEffect } from "react";
import { useLanguage } from "../../context/LanguageContext";
import {
  getCorporateBuyers,
  deleteCorporateBuyer,
} from "../../services/coporateIndividualBuyerServices.ts";
import { Search, Mail, Phone } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Pagination from "../common/Pagination";
import { Edit, Trash2 } from "lucide-react";
import { useToast } from "../../context/ToastContext";

const AllUser = () => {
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { showSuccess, showError } = useToast();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      setIsDeleting(true);
      await deleteCorporateBuyer(userToDelete.buyerId);
      await fetchUsers();
      setUsers(users.filter((u) => u.buyerId !== userToDelete.buyerId));

      // Decrement totalItems
      setPagination((prev) => ({
        ...prev,
        totalItems: prev.totalItems - 1,
      }));
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      showSuccess(
        translate("allUser.deleteSuccess") || "User deleted successfully",
      );
    } catch (err) {
      console.error("Error deleting user:", err);
      showError(translate("allUser.deleteError") || "Failed to delete user");
    } finally {
      setIsDeleting(false);
    }
  };
  // 1. Debounce search input (Matches WonVehicles logic)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // 2. Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const params = {
        page: currentPage,
        limit: pageSize,
      };

      if (debouncedSearch?.trim()) {
        params.search = debouncedSearch.trim();
      }

      const response = await getCorporateBuyers(params);
      console.log("API response for getCorporateBuyers:", response);

      setUsers(response.data || []);

      setPagination({
        currentPage: response.page,
        totalPages: response.pages,
        totalItems: response.total,
        itemsPerPage: pageSize,
      });
    } catch (err) {
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, debouncedSearch, pageSize]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  const filteredUsers = users.filter((u) => u && u.user);

  return (
    <div
      className="bg-card rounded-xl shadow-sm overflow-hidden"
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* Header */}
      <div className="bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px]">
        {/* Standardized Header Container with restored height for sub-text */}
        <div className="flex flex-row items-center justify-between px-4 sm:px-5 py-3 sm:py-4 rounded-t-[8px] sm:rounded-t-[12px] bg-primary-soft gap-3 min-h-[72px]">
          {/* Left Side: Heading & Sub-heading (Total restored) */}
          <div className="flex flex-col justify-center">
            <h2 className="text-white text-base sm:text-lg font-semibold whitespace-nowrap">
              {translate("dashboard.nav.allBuyers") || "All Buyers"}
            </h2>
            <span className="text-[10px] sm:text-xs font-medium text-white/60">
              {/* {translate("dashboard.addUser.total") || "Total"}: {pagination.totalItems} */}
              {translate("dashboard.addUser.total") || "Total"}:{" "}
              {filteredUsers.length}
            </span>
          </div>

          {/* Right Side: Search and Action Button */}
          <div className="flex flex-row items-center gap-2 ms-auto">
            {/* Pill-style Search Input */}
            <div className="relative w-full md:w-60 xl:w-72">
              <Search
                className={`absolute ${isRTL ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/70`}
              />
              <input
                type="text"
                placeholder={
                  translate("allUser.searchPlaceholder") || "Search user..."
                }
                className={`w-full px-3 py-1.5 ${isRTL ? "pr-9" : "pl-9"} bg-black/10 border border-white/10 rounded-full text-white placeholder-white/50 focus:outline-none focus:ring-1 focus:ring-white/30 text-xs transition-all`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Action Button */}
            <button
              onClick={() => navigate("/buyeruser")}
              className="flex-shrink-0 px-4 py-1.5 bg-white text-primary rounded-full font-semibold text-[10px] sm:text-xs shadow-md transition-all active:scale-95 whitespace-nowrap hover:bg-white/90"
            >
              {translate("dashboard.addUser.addbuyer") || "Add Buyer"}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="relative min-h-[300px]">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="py-20 text-center text-red-500">{error}</div>
        ) : filteredUsers.length === 0 ? (
          <div className="py-20 text-center text-description">
            No buyers found
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-page border-b border-default">
                    {[
                      "Full Name",
                      "Email",
                      "Phone",
                      "Credit Limit",
                      "Car Limit",
                      "Action",
                    ].map((h, i) => (
                      <th
                        key={i}
                        className={`px-6 py-4 text-[11px] uppercase tracking-wider font-bold text-white ${isRTL ? "text-right" : "text-left"}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="">
                  {filteredUsers.map((u, index) => (
                    <tr
                      key={u.id || index}
                      className="border-b border-default hover:bg-page/60 transition-colors"
                    >
                      <td className="px-6 py-4 text-sm font-medium text-white">
                        {u.user?.name || u.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-description">
                        {u.user?.email || u.email}
                      </td>
                      <td
                        className="px-6 py-4 text-sm text-description"
                        dir="ltr"
                      >
                        {u.user?.mobileNo || u.mobileNo || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-sm text-description font-semibold">
                        {u.buyerLimit?.remainingCreditLimit || 0}
                      </td>
                      <td className="px-6 py-4 text-sm text-description">
                        {u.buyerLimit?.remainingCarLimit || 0}
                      </td>
                      {/* <td className="px-6 flex gap-2 py-4">
                        <Link to={`/buyeruser/${u.user?.userId || u.userId || u.id}`} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors">
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          title={translate("allUser.table.delete") || "Delete"}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          // onClick={() => handleDeleteUser(u.buyerId)}
                          onClick={() => {
                            setUserToDelete(u);
                            setShowDeleteConfirm(true);
                          }}

                        >
                          <Trash2 className="w-4 h-4" />
                        </button>


                      </td> */}
                   <td className="px-6 py-4">
  <div className="flex flex-row items-center justify-start gap-3 min-h-[40px]">
    
    {/* 1. EDIT BUTTON - Expands Left */}
    <div className="relative h-10 w-10">
      <Link
        to={`/buyeruser/${u.user?.userId || u.userId || u.id}`}
        className="absolute right-0 top-0 h-10 w-10 hover:w-20 flex items-center justify-end bg-primary text-white rounded-lg transition-all duration-300 ease-in-out shadow-xl overflow-hidden z-10 hover:z-30 group"
      >
        <div className="flex flex-row-reverse items-center h-full w-full bg-primary group-hover:bg-primary-dark transition-colors duration-300">
          {/* Icon Container: Fixed on the right */}
          <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
            <Edit size={20} className="text-white" />
          </div>
          {/* Text: Slides out to the left */}
          <span className="pl-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm font-semibold text-white whitespace-nowrap">
            {translate("allUser.table.edit") || "Edit"}
          </span>
        </div>
      </Link>
    </div>

    {/* 2. DELETE BUTTON - Expands Left */}
    <div className="relative h-10 w-10">
      <button
        onClick={() => {
          setUserToDelete(u);
          setShowDeleteConfirm(true);
        }}
        className="absolute right-0 top-0 h-10 w-10 hover:w-28 flex items-center justify-end bg-red-50 border border-red-200 text-red-600 rounded-lg transition-all duration-300 ease-in-out shadow-xl overflow-hidden z-10 hover:z-30 group"
      >
        <div className="flex flex-row-reverse items-center h-full w-full bg-red-50 group-hover:bg-white transition-colors duration-300">
          {/* Icon Container: Fixed on the right */}
          <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
            <Trash2 size={20} />
          </div>
          {/* Text: Slides out to the left */}
          <span className="pl-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm font-semibold whitespace-nowrap">
            {translate("allUser.table.delete") || "Delete"}
          </span>
        </div>
      </button>
    </div>

  </div>
</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {showDeleteConfirm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center px-4 sm:px-6">
                {/* Backdrop */}
                <div
                  className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
                  onClick={() => !isDeleting && setShowDeleteConfirm(false)}
                />

                {/* Modal Card */}
                <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm sm:max-w-md mx-auto overflow-hidden">
                  {/* Header */}
                  <div className="px-6 py-4  border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 text-center sm:text-left">
                      {translate("allUser.deleteConfirmTitle") ||
                        "Confirm account deletion"}
                    </h3>
                  </div>

                  {/* Body */}
                  <div className="px-6 py-5 space-y-2 text-gray-700 text-center sm:text-left">
                    <p className="font-medium">
                      {translate("allUser.deleteConfirmText") ||
                        "Are you sure?"}
                    </p>
                    <p className="text-sm text-gray-600">
                      {translate("allUser.deleteWarning") ||
                        "This will permanently delete this user and cannot be undone."}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="px-6 py-4 bg-gray-50 flex flex-col sm:flex-row justify-end gap-3">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-white transition font-medium disabled:opacity-50"
                      disabled={isDeleting}
                    >
                      {translate("allUser.cancel") || "Cancel"}
                    </button>
                    <button
                      onClick={handleDeleteUser}
                      disabled={isDeleting}
                      className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium disabled:opacity-50"
                    >
                      {isDeleting
                        ? translate("allUser.deleting") || "Deleting..."
                        : translate("allUser.confirmDelete") || "Yes, delete"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4 p-4 bg-page">
              {filteredUsers.map((u, index) => (
                <div
                  key={u.id || index}
                  className="bg-card p-4 rounded-xl border border-default shadow-sm"
                >
                  <h3 className="font-bold float-left text-white">
                    {u.user?.name || u.name}
                  </h3>

                  <div className="flex justify-end items-start mb-2">
                    <Link
                      to={`/buyeruser/${u.user?.userId || u.id}`}
                      className="p-2 bg-white rounded-lg text-blue-600"
                    >
                      <Edit className="w-4 h-4" />
                    </Link>

                    <button
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      onClick={() => {
                        setUserToDelete(u);
                        setShowDeleteConfirm(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="text-sm text-white/70 space-y-1">
                    <p className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5" />
                      {u.user?.email || u.email}
                    </p>
                    <p className="flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5" />
                      {u.user?.mobileNo || u.mobileNo}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination (Matching WonVehicles Condition) */}
      {pagination.totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 bg-white">
          <Pagination
            totalPages={pagination.totalPages}
            currentPage={pagination.currentPage}
            setCurrentPage={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default AllUser;
